import React, { useState, useEffect } from "react";
import { Card, Col, Row, Typography, Checkbox, Button, Divider } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import axios from "axios";
import CompanyContacts from "components/EditCompanyContacts/CompanyContacts";
import { isCompanyUser, useLoginContext } from "contexts/LoginContext/LoginContext";
import { getWeek } from "helpers/functions";
import { CompanyContact } from "interfaces/companies";
import { BookingSeason } from "../interface";
import Terms from "./Terms";

interface Props {
  season: BookingSeason | undefined;
  setAccepted: (accepted: boolean) => void;
  goToNextStep: () => void;
}

const Information: React.FC<Props> = ({ season, setAccepted, goToNextStep }) => {
  const { Title, Text, Link } = Typography;
  const [contactPersons, setContactPersons] = useState<CompanyContact[]>([]);
  const [currentContact, setCurrentContact] = useState<CompanyContact | undefined>();
  const [contactsOpen, setContactsOpen] = useState<boolean>(false);

  const handleOpenContacts = () => setContactsOpen(!contactsOpen);

  const handleTerms = (e: CheckboxChangeEvent) => {
    setAccepted(e.target.checked);
  };

  const { currentUser } = useLoginContext();

  useEffect(() => {
    axios
      .get<CompanyContact[]>(`/companies/${currentUser?.id}/contact-persons/`)
      .then(response => setContactPersons(response.data));
  }, [currentUser]);

  useEffect(() => {
    const mainContacts = contactPersons.filter(contact => contact.main_contact);
    if (mainContacts.length > 0) {
      setCurrentContact(mainContacts[0]);
    }
  }, [contactPersons]);

  if (!isCompanyUser(currentUser)) return null;
  return (
    <>
      <Card style={{ width: "100%" }}>
        <Row gutter={25}>
          <Col xs={24} sm={24} md={12}>
            <Row>
              <Title level={5}>Når kan vi arrangere bedriftspresentasjon?</Title>
            </Row>
            <Row>
              {season ? (
                <Text>
                  Bedriftspresentasjonssesongen for {season.is_fall ? "høsten" : "våren"} {season.year} varer fra uke{" "}
                  {getWeek(new Date(season.semester_start))} til og med uke {getWeek(new Date(season.semester_end))},
                  med unntak av noen enkelte datoer. I bookingkalenderen på neste side kan man se mer nøyaktig hvilke
                  datoer som er tilgjengelige.
                </Text>
              ) : (
                <Text>Mer informasjon kommer snart.</Text>
              )}
            </Row>
            <br />
            <Row>
              <Title level={5}>Hvilken ukedag bør vi velge?</Title>
            </Row>
            <Row>
              <Text>
                Av erfaring er mandag, tirsdag og onsdag passende for mellomstore presentasjoner for mellom 20 og 60
                studenter, mens torsdag og fredag er passende for større presentasjoner. <br /> <br />
                På fredager vil selve presentasjonen vare fra 12:15-13:00, og fra 17:15-18:00 øvrige ukedager. På
                torsdager pleier mange studenter å dra ut på byen, derfor velger mange bedrifter å ha alkoholservering
                som en del av bespisningen. Det er flere måter å gjøre dette på også, litt avhengig av hvor bespisningen
                holdes. <br /> <br /> Spør oss gjerne om tips for hvilken dag som passer deres opplegg best.
                <br />
                <br />
              </Text>
            </Row>
            <Row>
              <Title level={5}>Hva er inkludert når vi booker bedriftspresentasjon?</Title>
            </Row>
            <Row>
              <Text>
                <ul>
                  <li>Bestilling av egnede lokaler til presentasjon og bespisning.</li>
                  <li>Bistand i planlegging av presentasjon og sosialt opplegg.</li>
                  <li>Markedsføring av presentasjonen gjennom plakater og websider.</li>
                  <li>Kvalitetssikring av det tekniske opplegget.</li>
                  <li>
                    Webbasert påmeldingssystem med mulighet for å sette av plasser for ønskede linjer og klassetrinn.
                  </li>
                  <li>Statistikk over påmeldte studenter.</li>
                  <li>Stand sentralt på campus.</li>
                  <li>Skriftlig tilbakemelding om bedriftens presentasjon.</li>
                </ul>
              </Text>
            </Row>
          </Col>
          <Col xs={24} sm={24} md={12}>
            <Row>
              <Title level={5}>Hvilket opplegg bør vi gjennomføre?</Title>
            </Row>
            <Row>
              <Text>
                Ved booking av bedriftspresentasjon vil vi hjelpe til med å planlegge arrangementet i samarbeid med
                dere. Presentasjon vil kanskje måtte holdes enten digitalt eller på et egnet sted utenfor campusområdet.
                Ved valg av sistnevnte er vi gjerne behjelpelige med å finne egnet sted for både presentasjon og
                bespisning.
                <br />
                Hvis forholdene tilater: Bespisningen kan enten være på campus, i Kjelkantinen, eller holdes på en
                restaurant i Trondheim. Det er også mulig å avholde selve presentasjonen eksternt på samme sted som
                bespisningen. På dagtid kan dere stå på stand eller gjennomføre arrangementer som intervjuer, mingling
                eller case.
              </Text>
            </Row>
            <br />
            <Row>
              <Title level={5}>Hvilke studenter når vi ut til?</Title>
            </Row>
            <Row>
              <Text>
                Bindeleddet NTNU er bedriftskontakten for Indøk, men vi samarbeider tett med de andre linjeforeningene
                på Gløshaugen. Å åpne presentasjonen for andre linjer gjør vi gledelig, men vi har et krav om at minst
                halvparten av plassene skal være reservert for indøkstudenter. Markedsføring mot de andre linjene utover
                informasjon i arrangementet på våre egne nettsider kan gjøres mot et gebyr til den respektive
                linjeforening.
              </Text>
            </Row>
            <br />
            <Row>
              <Title level={5}>Hvor mye koster det å arrangere bedriftspresentasjon?</Title>
            </Row>
            <Row>
              <Text>
                I vår prismodell finnes tre ulike pristrinn (differensiert på tidspunkt i sesongen).
                <br />
                {season?.price_model ? <img src={season.price_model} alt="Prismodell" style={{ width: "100%", maxWidth: "670px" }}/> : null}
                Prisene er i NOK og ekskludert mva. Differansen i pris mellom de ulike trinnene kommer hovedsakelig av
                at antall oppmøtte studenter kan synke noe mot slutten av sesongen. Torsdager har et ekstra tillegg
                ettersom dette er en svært populær dag for studenter å dra på bedriftspresentasjon. Ellers vil vi
                understreke at tilbudet fra Bindeleddet fremdeles er helt likt på de tre trinnene.
                <br />
                <br />
              </Text>
            </Row>
            <Row>
              <Title level={5}>Andre spørsmål?</Title>
            </Row>
            <Row>
              <Text>
                Mer informasjon finner dere under fanen “Booking og priser”. <br /> Dersom dere har andre spørsmål kan
                dere også sende oss en mail på <Link href="mailto:bedrift@bindeleddet.no">bedrift@bindeleddet.no</Link>
                .<br />
              </Text>
            </Row>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Title level={5}>Vilkår</Title>
          <Text type="secondary" style={{ margin: "2px 0 0 5px" }}>
            (Må godtas for å gå videre)
          </Text>
        </Row>
        <Row>
          <Checkbox onChange={handleTerms} style={{ borderRadius: "0px" }}>
            Jeg godtar <Terms />. <br /> <br />
          </Checkbox>
        </Row>
        <Row>
          <Title level={5}>Kontaktperson</Title>
        </Row>
        <Row>
          <Card bodyStyle={{ padding: "15px" }} style={{ width: "100%", backgroundColor: "var(--neutral-alt)" }}>
            {currentContact ? (
              <>
                <Text strong>
                  {currentContact.first_name} {currentContact.last_name}{" "}
                </Text>
                <Text>
                  er oppført som hovedkontakt hos {currentUser.name}, og vil bli registrert som kontaktperson for dette
                  arrangementet.
                </Text>
              </>
            ) : (
              <>
                <Text>
                  {currentUser.name} har ikke satt en hovedkontakt enda. Vennligst velg dette først, slik at vi enklere
                  kan nå dere når arrangementet nærmer seg.
                </Text>
              </>
            )}
            <Row>
              <Button type="primary" style={{ marginTop: "15px" }} onClick={handleOpenContacts}>
                {contactsOpen ? "Skjul kontaktpersoner" : currentContact ? "Endre kontaktperson" : "Velg kontaktperson"}
              </Button>
            </Row>

            <br />
            {contactsOpen && (
              <>
                <Title level={3}>Kontaktpersoner</Title>
                <CompanyContacts companyId={currentUser.id} onNewMain={c => setCurrentContact(c)} />
              </>
            )}
          </Card>
        </Row>
      </Card>
      <Row style={{ justifyContent: "space-between", width: "100%", padding: "10px 0" }}>
        <Button type="primary" danger size="large" href="/company/company-profile/">
          Avbryt
        </Button>
        <Button type="primary" size="large" onClick={goToNextStep}>
          Neste
        </Button>
      </Row>
    </>
  );
};

export default Information;
